import { EventType } from "@/analytics/analytics-events";
import { ToggleMultiSheetMenuProperties } from "@/analytics/multi-sheet-management-events-properties";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { FaroTooltip, LayersIcon, ToolButton } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { PopoverProps } from "@mui/material";
import { useCallback, useState, type JSX } from "react";
import { MultiSheetMenu } from "../multi-sheet-menu/multi-sheet-menu";
import { PdfReportSheetMenu } from "../multi-sheet-menu/pdf-report-sheet-menu";
import { ToolsTooltipText } from "../tools-toolbar";

export type MultiSheetToolProps = {
  /** Reference to the element to anchor the popover to */
  anchorEl: PopoverProps["anchorEl"];

  /** Reference to the element to anchor badge "new" */
  setButtonRef?: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;

  /**
   * Callback at pressing feature button in toolbar
   *
   * @param isVisible when it is true multi-sheet tool is visible
   */
  setMultiSheetToolVisible?(isVisible: boolean): void;
};

/**
 * @returns an icon button that open up a menu that allows to change which sheet should be rendered
 */
export function MultiSheetTool({
  anchorEl,
  setButtonRef,
  setMultiSheetToolVisible,
}: MultiSheetToolProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isPdfReportMode =
    useAppSelector(selectActiveTool) === ToolName.createPDFReport;

  const toggleMenu = useCallback(
    (shouldOpen: boolean) => {
      Analytics.track<ToggleMultiSheetMenuProperties>(
        EventType.toggleMultiSheetMenu,
        {
          opening: shouldOpen,
        },
      );

      setIsMenuOpen(shouldOpen);
      if (setMultiSheetToolVisible) setMultiSheetToolVisible(shouldOpen);
    },
    [setMultiSheetToolVisible],
  );

  return (
    <>
      <FaroTooltip placement="left" title={<ToolsTooltipText text="Sheets" />}>
        <ToolButton
          aria-label="Sheets"
          ref={setButtonRef}
          onClick={() => toggleMenu(!isMenuOpen)}
          selected={isMenuOpen}
        >
          <LayersIcon />
        </ToolButton>
      </FaroTooltip>
      {isPdfReportMode ? (
        <PdfReportSheetMenu
          open={isMenuOpen}
          anchorEl={anchorEl}
          onClose={() => toggleMenu(false)}
        />
      ) : (
        <MultiSheetMenu
          open={isMenuOpen}
          anchorEl={anchorEl}
          onClose={() => toggleMenu(false)}
        />
      )}
    </>
  );
}
